import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import {
  GUARANTEES_APPLICATION_CONTRAGENT_QUERY_PARAM,
  guaranteesApplicationStatusConfig,
  guaranteesClientText,
} from '~/modules/guarantees/form/client/constants.ts';
import {
  buildGuaranteesCounterpartyPath,
  getStringValue,
} from '~/modules/guarantees/utils.ts';
import {
  selectGuaranteesCurrentApplication,
  selectGuaranteesCurrentApplicationError,
  selectGuaranteesCurrentApplicationStatus,
} from '~/store/guarantees/selectors.ts';
import { fetchGuaranteesApplication } from '~/store/guarantees/thunks/applicationThunks.ts';
import { GuaranteesApplicationStatus, GuaranteesFetchStatus } from '~/store/guarantees/types.ts';
import {
  getGuaranteesApplicationDisplayNumber,
  getGuaranteesApplicationIdentity,
  normalizeGuaranteesApplicationStatus,
} from '~/store/guarantees/utils.ts';
import type { AppDispatch } from '~/store/store.ts';
import { useApplicationRequestFeedback } from '../../../shared/hooks/useApplicationRequestFeedback.ts';
import type { TClientStep2PageState } from '../types.ts';
import {
  getClientStep2ActionsDisabled,
  getClientStep2RedirectPath,
} from '../utils.ts';

export const useClientStep2Page = (): TClientStep2PageState => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { applicationId = '' } = useParams();
  const contragentId =
    new URLSearchParams(location.search).get(GUARANTEES_APPLICATION_CONTRAGENT_QUERY_PARAM) ?? '';
  const application = useSelector(selectGuaranteesCurrentApplication);
  const applicationStatus = useSelector(selectGuaranteesCurrentApplicationStatus);
  const applicationError = useSelector(selectGuaranteesCurrentApplicationError);
  const loadedApplicationId = getGuaranteesApplicationIdentity(application);
  const isLoadedApplicationCurrent = loadedApplicationId === applicationId;
  const currentRouteApplication = isLoadedApplicationCurrent ? application : null;
  const effectiveApplicationId =
    getStringValue(currentRouteApplication?.applicationId || currentRouteApplication?.id) ||
    applicationId;
  const {
    applicationNumber,
    isTemporaryApplicationNumber,
  } = getGuaranteesApplicationDisplayNumber(currentRouteApplication);
  const normalizedStatus = normalizeGuaranteesApplicationStatus(
    currentRouteApplication?.applicationStatus
  );
  const statusConfig =
    guaranteesApplicationStatusConfig[normalizedStatus] ??
    guaranteesApplicationStatusConfig[GuaranteesApplicationStatus.DRAFT];
  const isApplicationLoading =
    (applicationStatus === GuaranteesFetchStatus.Loading && !currentRouteApplication) ||
    (!!application && !isLoadedApplicationCurrent);
  const redirectPath = getClientStep2RedirectPath({
    application: currentRouteApplication,
    contragentId,
    effectiveApplicationId,
    normalizedStatus,
  });
  const isStepActionsDisabled = getClientStep2ActionsDisabled({
    application: currentRouteApplication,
    normalizedStatus,
  });
  const {
    modalAlert,
    modalSuccess,
    setModalAlert,
    setModalSuccess,
    showGuaranteesRequestError,
    handleCloseAlertModal,
    handleCloseSuccessModal,
  } = useApplicationRequestFeedback({
    applicationId,
    contragentId,
    dispatch,
  });
  const headerProps = useMemo(
    () => ({
      applicationNumber,
      applicationNumberTooltip: isTemporaryApplicationNumber
        ? guaranteesClientText.temporaryApplicationNumberTooltip
        : undefined,
      applicationNumberLabel: guaranteesClientText.applicationsNumberLabel,
      applicationIdLabel: guaranteesClientText.applicationIdLabel,
      backLabel: guaranteesClientText.backToApplicationsButton,
      contragentId,
      contragentIdLabel: guaranteesClientText.contragentIdLabel,
      effectiveApplicationId,
      onBack: () => navigate(buildGuaranteesCounterpartyPath(contragentId, false)),
      statusConfig,
    }),
    [
      applicationNumber,
      contragentId,
      effectiveApplicationId,
      isTemporaryApplicationNumber,
      navigate,
      statusConfig,
    ]
  );

  useEffect(() => {
    if (!contragentId || !applicationId || applicationId === 'new') {
      return;
    }

    void dispatch(fetchGuaranteesApplication({ contragentId, applicationId }));
  }, [applicationId, contragentId, dispatch]);

  return {
    applicationError,
    contragentId,
    effectiveApplicationId,
    headerProps,
    isApplicationLoading,
    isStepActionsDisabled,
    modalAlert,
    modalSuccess,
    redirectPath,
    setModalAlert,
    setModalSuccess,
    showGuaranteesRequestError,
    onModalClose: handleCloseAlertModal,
    onSuccessModalClose: handleCloseSuccessModal,
  };
};
